import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import {
    Router,
    RouterEvent,
    RouteConfigLoadStart,
    RouteConfigLoadEnd,
    ActivatedRoute,
} from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';

import { ContentService } from '@common/services/content.service';
import { ControlWithErrors } from '@common/infrastructure/form-tools';
import { PhaseJpService } from '@lang/jp/services/phasejp.service';

import { RegistrationService } from '@spjp/services/registration.service';
import { RoofPitch } from '@spjp/model/registration';
import { SolarService } from '@spjp/services/solar/solar.service';
import { ContentHelperService } from '@spjp/services/contenthelper.service';
import { SpjpCommunityService } from '@spjp/services/community.service';
import { SpjpCommunity } from '@spjp/model/community';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
    private destroyed$ = new Subject<void>();

    participantCount$: Observable<number>;
    hero: string;
    isB2B = false;
    registerButtonClicked: boolean;
    isLoadingRegister: boolean;
    interestedIndividualEnabled = false;
    hero$: Observable<string> = of('#');
    heroMobile$: Observable<string> = of('#');

    RoofPitch: typeof RoofPitch = RoofPitch;
    form: UntypedFormGroup;

    private participantCountCmsPrefix = 'participant-count-discount-item';

    discountRateItems = [
        {
            h6Key: this.participantCountCmsPrefix + '-panel-only-title',
            iconKey: this.participantCountCmsPrefix + '-panel-only-icon',
            kwKey: this.participantCountCmsPrefix + '-panel-only-kw',
            kwSecondKey: this.participantCountCmsPrefix + '-panel-only-kw-2',
            discountKey:
                this.participantCountCmsPrefix + '-panel-only-discount',
            discountSecondKey:
                this.participantCountCmsPrefix + '-panel-only-discount-2',
        },
        {
            h6Key: this.participantCountCmsPrefix + '-panel-and-battery-title',
            iconKey: this.participantCountCmsPrefix + '-panel-and-battery-icon',
            kwKey: this.participantCountCmsPrefix + '-panel-and-battery-kw',
            kwSecondKey:
                this.participantCountCmsPrefix + '-panel-and-battery-kw-2',
            discountKey:
                this.participantCountCmsPrefix + '-panel-and-battery-discount',
            discountSecondKey:
                this.participantCountCmsPrefix +
                '-panel-and-battery-discount-2',
        },
        {
            h6Key: this.participantCountCmsPrefix + '-battery-only-title',
            iconKey: this.participantCountCmsPrefix + '-battery-only-icon',
            kwKey: this.participantCountCmsPrefix + '-battery-only-kw',
            kwSecondKey: this.participantCountCmsPrefix + '-battery-only-kw-2',
            discountKey:
                this.participantCountCmsPrefix + '-battery-only-discount',
            discountSecondKey:
                this.participantCountCmsPrefix + '-battery-only-discount-2',
        },
    ];

    get roofPitch(): ControlWithErrors {
        return this.form.get('roofPitch');
    }

    public get participantCountTitle(): string {
        return this.interestedIndividualEnabled
            ? 'interested-individual-participant-count-title'
            : 'participant-count-title';
    }

    public get participantCountKey(): string {
        return this.interestedIndividualEnabled
            ? 'interested-individual-participant-count'
            : 'participant-count';
    }

    public get showVideo$(): Observable<boolean> {
        return this.contentService.valueExists('home-page-youtube-video');
    }

    public get showDiscountRate$(): Observable<boolean> {
        return this.contentService.valueExists(
            'participant-count-discount-rate-text',
        );
    }

    public hasSecondKwItem(index: number): Observable<boolean> {
        return this.contentService.valueExists(
            this.discountRateItems[index].kwSecondKey,
        );
    }

    public hasSecondDiscountItem(index: number): Observable<boolean> {
        return this.contentService.valueExists(
            this.discountRateItems[index].discountSecondKey,
        );
    }

    get showBatteryDetails(): boolean {
        return true;
    }

    get registrationEnabled$(): Observable<boolean> {
        return this.communityService.isCommunityRegistrationEnabled$;
    }

    constructor(
        public contentHelperService: ContentHelperService,
        public registrationService: RegistrationService,
        public contentService: ContentService,
        public communityService: SpjpCommunityService,
        public phaseService: PhaseJpService,
        private solarService: SolarService,
        private router: Router,
        private fb: UntypedFormBuilder,
    ) {
        this.form = this.fb.group({
            roofPitch: [null],
        });

        this.handleRegisterLazyLoadingSpinner();
        this.communityService.currentAuctionOpenForNewRegistrations$.subscribe(
            (isOpen) => (this.interestedIndividualEnabled = !isOpen),
        );
    }

    ngOnInit() {
        this.communityService.community$.subscribe(
            (community: SpjpCommunity) => {
                if (community) {
                    this.isB2B = community.isB2B;
                }
                if (community.targetAuction != null) {
                    this.participantCount$ =
                        this.registrationService.countSubscriptions(
                            community.targetAuction.id,
                        );
                } else {
                    this.participantCount$ = this.registrationService.count(
                        community.id,
                    );
                }
            },
        );
        this.hero$ = this.contentService
            .value('home-hero-banner-xl')
            .pipe(takeUntil(this.destroyed$));

        this.heroMobile$ = this.contentService
            .value('home-hero-banner-l')
            .pipe(takeUntil(this.destroyed$));

        this.toggleGetfeedbackBtnVisibility();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();

        this.toggleGetfeedbackBtnVisibility(true);
    }

    formatBulletLink(cmsKey: string): Observable<string> {
        return this.contentService.valueExists(cmsKey).pipe(
            switchMap((exists: boolean) => {
                if (exists) {
                    return this.contentService.value(cmsKey);
                } else {
                    return of('#'); // # as a fallback will stay on the same page and scroll to the top
                }
            }),
        );
    }

    private handleRegisterLazyLoadingSpinner() {
        this.isLoadingRegister = false;
        this.registerButtonClicked = false;
        let asyncLoadCount = 0;

        this.router.events.subscribe((event): void => {
            if (
                event instanceof RouteConfigLoadStart &&
                event.route.path === 'register'
            ) {
                asyncLoadCount++;
            } else if (
                event instanceof RouteConfigLoadEnd &&
                event.route.path === 'register'
            ) {
                asyncLoadCount--;
            }
            this.isLoadingRegister = !!asyncLoadCount;
        });
    }

    private toggleGetfeedbackBtnVisibility(visible: boolean = false) {
        const elem = this.getGetfeedbackBtnElement();
        if (!!elem) {
            elem.style.display = visible ? '' : 'none';
        }
    }

    private getGetfeedbackBtnElement() {
        return document.getElementsByClassName(
            'usabilla-live-button',
        )[0] as HTMLElement;
    }
}
